// src/oura/components/newscan/TownPicker.tsx
//
// The Town on an outlet row: the town customers type when they search
// ("dentist in Puchong"), never a state. A button that opens a searchable
// list: towns this business already uses first, then Klang Valley and the
// rest of Malaysia. Typing a town that is not listed offers "Use <town>".
// "Kuala Lumpur" and "KL" are one town, written KL (see towns.ts).

import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@component/command";
import { cn } from "@oura/lib/cn";
import { TOWN_GROUPS, isStateName, knownTown, townSpelling } from "@oura/newscan/towns";

export interface TownPickerProps {
    value: string;
    /** Towns the business's other places use, offered first. */
    inUse: string[];
    label: string;
    disabled?: boolean;
    className?: string;
    onChange(town: string): void;
}

const COPY = {
    search: "Search or type a town",
    inUse: "Used by this business",
    pickTyped: (town: string) => `Use "${town}"`,
    none: "Type the town customers search with.",
    pick: "Pick a town",
    state: (state: string) => `${state} is a state. Pick the town customers search with, like Puchong or Shah Alam.`,
} as const;



export function TownPicker({ value, inUse, label, disabled, className, onChange }: TownPickerProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const shown = townSpelling(value);
    const typed = query.trim().replace(/\s+/g, " ");
    const used = Array.from(new Set([shown, ...inUse.map(townSpelling)].filter(Boolean)));
    // A typed town nobody lists yet ("Bangi Lama"): offer it as typed. A known spelling ("JB") is offered as the listed one.
    const isState = isStateName(typed);
    const custom = typed && !isState && !knownTown(typed) && !used.some((t) => t.toLowerCase() === typed.toLowerCase()) ? typed : null;
    const alias = typed ? knownTown(typed) : null;

    const pick = (town: string) => {
        setOpen(false);
        setQuery("");
        if (town !== value) onChange(town);
    };

    const item = (t: string) => (
        <CommandItem key={t} value={t} onSelect={() => pick(t)} className="text-sm">
            <span className="flex-1">{t}</span>
            {t === shown && <CheckIcon aria-hidden className="text-gray-700" />}
        </CommandItem>
    );

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setQuery("");
            }}
        >
            <PopoverTrigger asChild disabled={disabled}>
                <button
                    type="button"
                    data-town-trigger=""
                    aria-label={label}
                    className={cn(
                        "flex items-center justify-between border border-input px-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed",
                        className,
                    )}
                >
                    <span className={cn("truncate", !shown && "text-gray-500")}>{shown || COPY.pick}</span>
                    <ChevronDownIcon aria-hidden className="size-4 shrink-0 opacity-50" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
                <Command
                    filter={(itemValue, search, keywords) => {
                        const s = search.trim().toLowerCase();
                        if (!s) return 1;
                        // The alias row leads while its text is typed; "Use" always shows, below any listed match.
                        if (keywords?.includes("__alias__")) return 1;
                        if (keywords?.includes("__use__")) return 0.05;
                        const v = itemValue.toLowerCase();
                        return v.startsWith(s) ? 0.9 : v.includes(s) ? 0.5 : 0;
                    }}
                >
                    <CommandInput placeholder={COPY.search} value={query} onValueChange={setQuery} />
                    <CommandList className="max-h-72">
                        <CommandEmpty className="px-3 py-4 text-sm text-gray-500">{isState ? COPY.state(typed) : COPY.none}</CommandEmpty>
                        {alias && !alias.toLowerCase().startsWith(typed.toLowerCase()) && (
                            <CommandGroup>
                                <CommandItem value={`__alias__${alias}`} keywords={["__alias__"]} onSelect={() => pick(alias)} className="text-sm">
                                    {alias}
                                </CommandItem>
                            </CommandGroup>
                        )}
                        {used.length > 0 && <CommandGroup heading={COPY.inUse}>{used.map(item)}</CommandGroup>}
                        {TOWN_GROUPS.map((g) => {
                            const rest = g.towns.filter((t) => !used.includes(t));
                            return rest.length ? (
                                <CommandGroup key={g.label} heading={g.label}>
                                    {rest.map(item)}
                                </CommandGroup>
                            ) : null;
                        })}
                        {custom && (
                            <CommandGroup>
                                <CommandItem value={`__use__${custom}`} keywords={["__use__"]} onSelect={() => pick(custom)} className="text-sm">
                                    {COPY.pickTyped(custom)}
                                </CommandItem>
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
